import React, { useState, useEffect, useRef } from 'react';
import invoiceService from 'services/invoiceService';
import Swal from 'sweetalert2';
import 'styles/provider.css';
import { useNavigate } from "react-router-dom";
import { useAuth } from 'hooks/useAuth';

export const InvoicesView = ({ isAdmin: isAdminProp }) => {
  const navigate = useNavigate();
  const { getUserData } = useAuth();
  const user = getUserData();
  const isAdmin = isAdminProp !== undefined ? isAdminProp : user?.rol === 'administrador';

  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  // Solo la primera carga muestra el spinner de pantalla completa
  const [initialLoad, setInitialLoad] = useState(true);
  // Evita que una respuesta vieja sobrescriba una más reciente
  const requestIdRef = useRef(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalCount: 0, totalPages: 0 });

  // ── Selección para descarga masiva (solo admin) ──────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // ── Modal complemento de pago (proveedor, PPD) ───────────────────────────
  const [uploadingComplemento, setUploadingComplemento] = useState(false);

 useEffect(() => {
  loadInvoices();
}, [activeFilter, pagination.page, debouncedSearch]);

const loadInvoices = async () => {
  const requestId = ++requestIdRef.current;
  try {
    setLoading(true);

    const params = {
      page: pagination.page,
      limit: pagination.limit,
    };

    if (activeFilter !== 'all') {
      params.estatus = activeFilter;
    }

    // 🔍 búsqueda backend
    if (debouncedSearch.trim()) {
  params.search = debouncedSearch.trim();
}

    const response = await invoiceService.getInvoices(params);
    if (requestId !== requestIdRef.current) return; // respuesta vieja

    if (!response.error) {
      setInvoices(response.data.data || []);

      setPagination(prev => ({
        ...prev,
        totalCount: response.data.pagination?.totalCount || 0,
        totalPages: response.data.pagination?.totalPages || 0
      }));
    }

  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar las facturas',
      timer: 3000
    });
  } finally {
    if (requestId === requestIdRef.current) {
      setLoading(false);
      setInitialLoad(false);
    }
  }
};

  // Debounce búsqueda backend
   useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(searchTerm);
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  }, 500);

  return () => clearTimeout(timer);
}, [searchTerm]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDate = (s) => {
    if (!s) return '';
    return new Date(s).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const formatCurrency = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

  const statusTranslations = {
    recibida: 'Recibida', en_revision: 'En Revisión', aprobada: 'Aprobada',
    rechazada: 'Rechazada', pagada: 'Pagada', incumplida: 'Incumplida'
  };

  const filters = [
    { id: 'all', label: 'Todas' },
    { id: 'recibida', label: 'Recibidas' },
    { id: 'en_revision', label: 'En Revisión' },
    { id: 'aprobada', label: 'Aprobadas' },
    { id: 'rechazada', label: 'Rechazadas' },
    { id: 'pagada', label: 'Pagadas' },
    { id: 'incumplida', label: 'Incumplidas' },
  ];

  // ── Descarga individual ───────────────────────────────────────────────────
  const handleDownload = async (invoiceId, tipo) => {
    try {
      await invoiceService.downloadInvoiceFile(invoiceId, tipo);
      Swal.fire({ icon: 'success', title: 'Descarga iniciada', timer: 2000, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar el archivo', timer: 3000 });
    }
  };

  // ── Ver detalles (incluye factura firmada si existe) ───────────────────────
  const handleViewDetails = async (invoice) => {
    let inv = invoice;
    const details = await invoiceService.getInvoiceById(invoice.id);
    if (!details.error) inv = details.data.data;

    const firmadaLink = inv.factura_firmada_url
      ? `<p><strong>Factura Firmada:</strong> <a href="${inv.factura_firmada_url}" target="_blank" style="color:#007bff">📄 Ver documento</a></p>`
      : '';
    const complementoLink = inv.complemento_pago_url
      ? `<p><strong>Complemento de Pago:</strong> <a href="${inv.complemento_pago_url}" target="_blank" style="color:#007bff">📄 Ver complemento</a></p>`
      : '';
    const comprobanteLink = inv.comprobante_pago_url
      ? `<p><strong>Comprobante de Pago:</strong> <a href="${inv.comprobante_pago_url}" target="_blank" style="color:#007bff">📄 Ver comprobante</a></p>`
      : '';
    const ppd = inv.metodo_pago === 'PPD'
      ? `<p><strong>Método de Pago SAT:</strong> <span style="color:#856404;background:#fff3cd;padding:2px 8px;border-radius:4px">PPD</span></p>`
      : inv.metodo_pago === 'PUE'
        ? `<p><strong>Método de Pago SAT:</strong> <span style="color:#155724;background:#d4edda;padding:2px 8px;border-radius:4px">PUE</span></p>`
        : '';
    const fechaLimite = inv.fecha_limite_complemento
      ? `<p><strong>Fecha límite complemento:</strong> ${formatDate(inv.fecha_limite_complemento)}</p>`
      : '';

    Swal.fire({
      title: `Factura ${inv.folio}`,
      html: `
        <div style="text-align:left;max-height:480px;overflow-y:auto;padding-right:4px">
          <p><strong>UUID:</strong> ${inv.uuid}</p>
          <p><strong>Proveedor:</strong> ${inv.proveedor_nombre || 'N/A'}</p>
          <p><strong>RFC Emisor:</strong> ${inv.rfc_emisor}</p>
          <p><strong>RFC Receptor:</strong> ${inv.rfc_receptor}</p>
          <p><strong>Fecha:</strong> ${formatDate(inv.fecha)}</p>
          ${ppd}
          <p><strong>Subtotal:</strong> ${formatCurrency(inv.subtotal)}</p>
          <p><strong>IVA:</strong> ${formatCurrency(inv.iva)}</p>
          <p><strong>Total:</strong> ${formatCurrency(inv.total)}</p>
          <p><strong>Centro de Costo:</strong> ${inv.centro_costo_nombre || 'N/A'}</p>
          <p><strong>Tipo de Gasto:</strong> ${inv.tipo_gasto_nombre || 'N/A'}</p>
          ${inv.notas ? `<p><strong>Notas:</strong> ${inv.notas}</p>` : ''}
          ${inv.notas_rechazo ? `<p style="color:#dc3545"><strong>Notas de Rechazo:</strong> ${inv.notas_rechazo}</p>` : ''}
          ${fechaLimite}
          <hr style="margin:12px 0"/>
          <p style="font-weight:700;margin-bottom:6px">📎 Documentos</p>
          ${firmadaLink}
          ${comprobanteLink}
          ${complementoLink}
        </div>`,
      width: 640,
      confirmButtonText: 'Cerrar'
    });
  };

  // ── Selección masiva (admin) ──────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map(inv => inv.id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Sin selección', text: 'Selecciona al menos una factura', timer: 2500 });
      return;
    }
    const result = await Swal.fire({
      title: 'Descarga masiva',
      html: `<p>Se descargarán <strong>${selectedIds.length}</strong> factura(s) con todos sus documentos en un archivo ZIP.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Descargar ZIP',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#8b6f47'
    });
    if (!result.isConfirmed) return;
    try {
      setDownloading(true);
      Swal.fire({ title: 'Generando ZIP...', text: 'Esto puede tomar un momento', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res = await invoiceService.bulkDownload({ factura_ids: selectedIds });
      Swal.close();
      if (!res.error) {
        Swal.fire({ icon: 'success', title: '¡Descarga lista!', text: 'El archivo ZIP se ha descargado correctamente', timer: 3000, showConfirmButton: false });
        setSelectedIds([]);
      } else {
        throw new Error('Error en la descarga');
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar la descarga masiva', timer: 3000 });
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadComplemento = async (invoice) => {
    const result = await Swal.fire({
      title: 'Subir Complemento',
      html: `
      <div style="text-align:left">
        <p><strong>Factura:</strong> ${invoice.folio}</p>
        <div style="
          background:#e7f3ff;
          border:1px solid #007bff;
          border-radius:8px;
          padding:12px;
          margin:12px 0;
          font-size:0.9rem;
          color:#004085;">
          Sube el archivo <strong>XML del Complemento de Pago</strong> (CFDI 3.3 / 4.0).
        </div>

        <label style="display:block;margin-bottom:6px">
          <strong>Archivo XML:</strong> <span style="color:#dc3545">*</span>
        </label>

        <input 
          id="swal-complemento-file"
          type="file"
          accept=".xml"
          class="swal2-input"
          style="margin:0;width:100%;padding:8px"
        />
        <small style="color:#6c757d">
          Solo archivos .xml
        </small>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: 'Subir Complemento',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        const fileInput = document.getElementById('swal-complemento-file');

        if (!fileInput.files[0]) {
          Swal.showValidationMessage('Selecciona el archivo XML');
          return false;
        }

        return fileInput.files[0];
      }
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Subiendo...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const fd = new FormData();
      fd.append('complemento', result.value);

      const res = await invoiceService.uploadComplementoPago(invoice.id, fd);

      Swal.close();

      if (!res.error) {
        Swal.fire({
          icon: 'success',
          title: '¡Complemento registrado!',
          text: 'El complemento de pago se subió correctamente.',
          timer: 3000,
          showConfirmButton: false
        });

        loadInvoices();
      } else {
        throw new Error();
      }

    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo subir el complemento de pago',
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages)
      setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (initialLoad) {
    return (
      <div className="invoices-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="invoices-view">
        <div className="card">

          {/* ── Barra de búsqueda ── */}
          <div className="search-and-filter">
            <input
              type="text"
              className="search-box"
              placeholder="Buscar por folio, UUID, monto o proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* ── Filtros de estatus ── */}
          <div className="filter-group">
            {filters.map(f => (
              <button
                key={f.id}
                className={`filter-btn ${activeFilter === f.id ? 'active' : ''}`}
                onClick={() => { setActiveFilter(f.id); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* ── Toolbar descarga masiva (solo admin) ── */}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '12px 0', padding: '12px 16px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #dee2e6', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, color: '#495057', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={invoices.length > 0 && selectedIds.length === invoices.length}
                  onChange={toggleSelectAll}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Seleccionar todos ({invoices.length})
              </label>
              {selectedIds.length > 0 && (
                <span style={{ color: '#8b6f47', fontWeight: 600 }}>
                  {selectedIds.length} seleccionada(s)
                </span>
              )}
              <button
                className="btn"
                onClick={handleBulkDownload}
                disabled={selectedIds.length === 0 || downloading}
                style={{ marginLeft: 'auto', background: selectedIds.length > 0 ? '#8b6f47' : '#adb5bd', color: 'white', padding: '8px 18px', borderRadius: '8px', fontSize: '0.9rem', cursor: selectedIds.length > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <i className="bi bi-file-zip"></i>
                Descargar seleccionadas ({selectedIds.length})
              </button>
            </div>
          )}

          {/* ── Lista de facturas ── */}
          <div className="invoice-list" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            {invoices.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <div className="empty-text">{searchTerm ? 'No se encontraron facturas' : 'No hay facturas con este filtro'}</div>
              </div>
            ) : (
              invoices.map(invoice => (
                <div
                  key={invoice.id}
                  className={`invoice-item ${invoice.estatus === 'rechazada' ? 'invoice-rejected' : ''} ${invoice.estatus === 'incumplida' ? 'invoice-incumplida' : ''}`}
                  style={{ position: 'relative' }}
                >
                  {/* Checkbox admin */}
                  {isAdmin && (
                    <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 2 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(invoice.id)}
                        onChange={() => toggleSelect(invoice.id)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#8b6f47' }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}

                  <div className="invoice-info" style={isAdmin ? { paddingLeft: '36px' } : {}}>
                    <h3 className="invoice-folio">{invoice.folio}</h3>

                    {isAdmin && <p className="invoice-provider">{invoice.proveedor_nombre}</p>}

                    {/* Badge PPD/PUE */}
                    {invoice.metodo_pago && (
                      <span style={{
                        display: 'inline-block', fontSize: '0.75rem', fontWeight: 700,
                        padding: '2px 8px', borderRadius: '4px', marginBottom: '6px',
                        background: invoice.metodo_pago === 'PPD' ? '#fff3cd' : '#d4edda',
                        color: invoice.metodo_pago === 'PPD' ? '#856404' : '#155724',
                        border: `1px solid ${invoice.metodo_pago === 'PPD' ? '#ffc107' : '#28a745'}`
                      }}>
                        SAT: {invoice.metodo_pago}
                      </span>
                    )}

                    {/* Motivo rechazo */}
                    {invoice.estatus === 'rechazada' && invoice.notas_rechazo && (
                      <div className="rejection-notice">
                        <div className="rejection-icon">⚠️</div>
                        <div className="rejection-content">
                          <strong>Motivo del Rechazo:</strong>
                          <p>{invoice.notas_rechazo}</p>
                        </div>
                      </div>
                    )}

                    {/* Alerta incumplida */}
                    {invoice.estatus === 'incumplida' && (
                      <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span>⚠️</span>
                        <div>
                          <strong style={{ color: '#856404' }}>Complemento de Pago Vencido</strong>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#856404' }}>No se cargó el complemento de pago dentro del plazo. Los pagos subsecuentes pueden verse afectados.</p>
                        </div>
                      </div>
                    )}

                    {/* Alerta complemento pendiente PPD */}
                    {invoice.estatus === 'pagada' && invoice.metodo_pago === 'PPD' && !invoice.complemento_pago && !isAdmin && invoice.fecha_limite_complemento && (
                      <div style={{ background: '#e7f3ff', border: '1px solid #007bff', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span>📋</span>
                        <div>
                          <strong style={{ color: '#004085' }}>Complemento de Pago Requerido</strong>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#004085' }}>
                            Debes subir el Complemento de Pago antes del <strong>{formatDate(invoice.fecha_limite_complemento)}</strong>.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Complemento ya subido — proveedor puede verlo */}
                    {invoice.metodo_pago === 'PPD' && invoice.complemento_pago && !isAdmin && (
                      <div style={{ background: '#d4edda', border: '1px solid #28a745', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span>✅</span>
                        <div>
                          <strong style={{ color: '#155724' }}>Complemento de Pago Registrado</strong>
                          {invoice.complemento_pago_url && (
                            <p style={{ margin: 0, fontSize: '0.875rem' }}>
                              <a href={invoice.complemento_pago_url} target="_blank" rel="noopener noreferrer" style={{ color: '#155724' }}>
                                📄 Ver complemento
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Datos de la factura */}
                    <div className="invoice-details">
                      <div className="invoice-detail-item">
                        <span className="invoice-detail-label">Monto</span>
                        <span className="invoice-detail-value">{formatCurrency(invoice.total)}</span>
                      </div>
                      <div className="invoice-detail-item">
                        <span className="invoice-detail-label">Fecha</span>
                        <span className="invoice-detail-value">{formatDate(invoice.fecha)}</span>
                      </div>
                      <div className="invoice-detail-item">
                        <span className="invoice-detail-label">UUID</span>
                        <span className="invoice-detail-value invoice-uuid">{invoice.uuid?.substring(0, 20)}...</span>
                      </div>
                      <div className="invoice-detail-item">
                        <span className="invoice-detail-label">Centro de Costo</span>
                        <span className="invoice-detail-value">{invoice.centro_costo_nombre || 'N/A'}</span>
                      </div>
                      {invoice.fecha_pago_programada && (
                        <div className="invoice-detail-item">
                          <span className="invoice-detail-label">Pago programado</span>
                          <span className="invoice-detail-value">{formatDate(invoice.fecha_pago_programada)}</span>
                        </div>
                      )}
                      {invoice.fecha_limite_complemento && (
                        <div className="invoice-detail-item">
                          <span className="invoice-detail-label">Límite complemento</span>
                          <span className="invoice-detail-value" style={{ color: '#856404', fontWeight: 600 }}>
                            {formatDate(invoice.fecha_limite_complemento)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Botón actualizar factura rechazada (proveedor) */}
                    {invoice.estatus === 'rechazada' && !isAdmin && (
                      <div className="rejected-actions">
                        <button className="btn-update-invoice" onClick={() => navigate(`/invoice-update/${invoice.id}`)}>
                          🔄 Actualizar Factura
                        </button>
                        <p className="helper-text-small">Puedes actualizar los archivos y datos de esta factura rechazada</p>
                      </div>
                    )}


                    {/* Acciones de descarga */}
                    <div className="invoice-actions-row">
                      {/* Botón subir complemento PPD (proveedor) */}
                      {!isAdmin && invoice.estatus === 'pagada' && invoice.datos_xml.metodoPago === 'PPD' && !invoice.complemento_pago && (

                        <button
                          className="btn-action btn-xml"
                          onClick={() => handleUploadComplemento(invoice)}

                        >
                          <i className="bi bi-upload"></i> Subir Complemento de Pago
                        </button>

                      )}

                      <button className="btn-action btn-xml" onClick={() => handleViewDetails(invoice)}>
                        <i className="bi bi-eye"></i> Ver Detalles
                      </button>
                      <button className="btn-action btn-xml" onClick={() => handleDownload(invoice.id, 'xml')}>
                        <i className="bi bi-filetype-xml"></i> XML
                      </button>
                      <button className="btn-action btn-pdf" onClick={() => handleDownload(invoice.id, 'pdf')}>
                        <i className="bi bi-filetype-pdf"></i> PDF
                      </button>
                      <button className="btn-action btn-signed" onClick={() => handleDownload(invoice.id, 'firmado')}>
                        <i className="bi bi-patch-check"></i> Firmado
                      </button>
                      {invoice.comprobante_pago && (
                        <button className="btn-action" style={{ background: '#28a745', color: 'white' }} onClick={() => handleDownload(invoice.id, 'comprobante')}>
                          <i className="bi bi-receipt"></i> Comprobante
                        </button>
                      )}
                      {invoice.complemento_pago && (
                        <button className="btn-action" style={{ background: '#17a2b8', color: 'white' }} onClick={() => handleDownload(invoice.id, 'complemento')}>
                          <i className="bi bi-file-earmark-check"></i> Complemento
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="invoice-status">
                    <span className={`status-badge status-${invoice.estatus}`}>
                      {statusTranslations[invoice.estatus] || invoice.estatus}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}>← Anterior</button>
              <span className="pagination-info">Página {pagination.page} de {pagination.totalPages}</span>
              <button className="pagination-btn" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages}>Siguiente →</button>
            </div>
          )}
        </div>
      </div>


    </>
  );
};